import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Divider,
  Button,
  Paper
} from '@mui/material';
import { ViewColumn } from '@mui/icons-material';

interface Column {
  key: string;
  label: string;
  defaultVisible: boolean;
}

interface ColumnToggleProps {
  columns: Column[];
  storageKey: string;
  onChange?: (visibleColumns: string[]) => void;
}

export const ColumnToggle: React.FC<ColumnToggleProps> = ({ columns, storageKey, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  // Загрузка из localStorage
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setVisibleColumns(parsed);
        return;
      } catch (e) {}
    }
    // По умолчанию
    setVisibleColumns(columns.filter(c => c.defaultVisible).map(c => c.key));
  }, [columns, storageKey]);

  const handleToggle = (key: string) => {
    setVisibleColumns(prev => {
      const newState = prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key];
      localStorage.setItem(storageKey, JSON.stringify(newState));
      if (onChange) onChange(newState);
      return newState;
    });
  };

  const handleReset = () => {
    const defaults = columns.filter(c => c.defaultVisible).map(c => c.key);
    setVisibleColumns(defaults);
    localStorage.setItem(storageKey, JSON.stringify(defaults));
    if (onChange) onChange(defaults);
  };

  const handleSelectAll = () => {
    const all = columns.map(c => c.key);
    setVisibleColumns(all);
    localStorage.setItem(storageKey, JSON.stringify(all));
    if (onChange) onChange(all);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'column-toggle-popover' : undefined;

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-describedby={id}
        sx={{ ml: 1 }}
      >
        <ViewColumn fontSize="small" />
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Paper sx={{ p: 2, minWidth: 220, maxWidth: 280 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Колонки
            </Typography>
            <Box>
              <Button size="small" onClick={handleSelectAll}>Все</Button>
              <Button size="small" onClick={handleReset}>Сброс</Button>
            </Box>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <FormGroup>
            {columns.map((col) => (
              <FormControlLabel
                key={col.key}
                control={
                  <Checkbox
                    size="small"
                    checked={visibleColumns.includes(col.key)}
                    onChange={() => handleToggle(col.key)}
                  />
                }
                label={col.label}
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
            ))}
          </FormGroup>
        </Paper>
      </Popover>
    </>
  );
};
